import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@loot-os/ui/components/dialog";
import { Skeleton } from "@loot-os/ui/components/skeleton";
import { toast } from "@loot-os/ui/components/sonner";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef } from "react";

import { useTRPC } from "../lib/trpc";
import { CardsView } from "./packing/CardsView";

/**
 * TCG Order List's Cards View, over one order (View Cards) or the selection (Combined Card View):
 * every card of those orders with its status, packed and cancelled ones included, which is where
 * it differs from Packing's pick-sheet card view. Closed while `orderIds` is null.
 */
export function OrderCardsDialog({
  orderIds,
  onClose,
}: {
  orderIds: string[] | null;
  onClose: () => void;
}) {
  const trpc = useTRPC();
  const cards = useQuery({
    ...trpc.platform.fulfillment.orders.cards.queryOptions({ orderIds: orderIds ?? [] }),
    enabled: orderIds !== null && orderIds.length > 0,
  });
  const count = orderIds?.length ?? 0;

  // A failed read is a toast, the dashboard's feedback, and closes the dialog it would have filled.
  // Once per failed fetch: keyed on when it failed, not on `onClose` (a new arrow each render), and
  // skipped while a reopen's retry runs, so a cached error does not close the dialog again.
  const close = useRef(onClose);
  close.current = onClose;
  const failure = cards.isFetching ? undefined : cards.error?.message;
  const failedAt = cards.errorUpdatedAt;
  useEffect(() => {
    if (!failure || !failedAt) return;
    toast.error(`Could not load the cards: ${failure}`);
    close.current();
  }, [failure, failedAt]);

  return (
    <Dialog open={orderIds !== null} onOpenChange={(open) => (open ? undefined : onClose())}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-6xl">
        <DialogHeader>
          <DialogTitle>Cards View</DialogTitle>
          <DialogDescription>
            {count === 1 ? "Every card in this order" : `Every card in ${count} orders`}, packed and
            cancelled ones included.
          </DialogDescription>
        </DialogHeader>
        {cards.isLoading || cards.error ? (
          <Skeleton className="h-64 w-full" />
        ) : (
          <CardsView units={cards.data ?? []} showStatus />
        )}
      </DialogContent>
    </Dialog>
  );
}
